import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
}

interface ThemeProviderState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  ...props
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(defaultTheme);

  // Get theme from secure HTTP-only cookie via API
  useEffect(() => {
    async function fetchTheme() {
      try {
        const res = await fetch('/api/theme', { credentials: 'include' });
        const data = await res.json();
        if (['light', 'dark', 'system'].includes(data.theme)) {
          setThemeState(data.theme);
          applyThemeClass(data.theme);
        } else {
          applyThemeClass(defaultTheme);
        }
      } catch {
        applyThemeClass(defaultTheme);
      }
    }

    fetchTheme();
  }, [defaultTheme]);

  // Update the DOM class based on the theme
  const applyThemeClass = (theme: Theme) => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }
  };

  // Update the theme and call backend to set cookie
  const setTheme = async (newTheme: Theme) => {
    try {
      await fetch('/api/theme', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ theme: newTheme }),
      });
      setThemeState(newTheme);
      applyThemeClass(newTheme);
    } catch (error) {
      console.error('Failed to set theme', error);
    }
  };

  const value: ThemeProviderState = {
    theme,
    setTheme,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};